<template>
  <div
    class="text-filter"
    :class="{ 'is-overflowing': isOverflowing }"
  >
    <!--
      Note : le tooltip d'aide ( ? icon avec explication OR + insensible
      à la casse ) est rendu côté parent ( FiltersDataCollapse ) à côté
      du label de la colonne , pour rester aligné avec le pattern
      existant des filtres référence. Voir hasFilterTextTag dans
      FiltersDataCollapse.vue.

      Implémentation : input natif + chips manuelles ( pas de b-taginput ) :
        - on échappe à la prop "separator" implicite de b-taginput qui
          coupait la chip à chaque virgule ( cf. issue #1 ) ;
        - on évite la désynchronisation entre le model-value externe et
          la newTag ref interne de b-taginput , qui rendait l'ajout
          intermittent après une suppression ( re-add du même mot ne
          marchait que parfois , puis blocage complet - cf. issue #2 ).
        - tout l'état ( saisie courante , chips ) est local et déterministe.
    -->
    <div
      class="taginput-container"
      :class="{ 'is-disabled': hasSingleOption }"
    >
      <ul
        v-if="selectedValues.length > 0"
        class="text-filter-chips"
      >
        <li
          v-for="(tag, index) in selectedValues"
          :id="column.componentKey + '-_-' + tag"
          :key="index"
          class="tag is-rounded"
          :class="{
            'is-info': tag === emptyValueLabel,
            'is-primary': tag !== emptyValueLabel,
            'single-option-tag': hasSingleOption,
          }"
          :title="hasSingleOption ? $t('filter.list-only-option-available') : tag"
        >
          <!-- Span interne pour cap 20em + ellipse , identique au
               wrapper utilisé par ListFilter. Le layout de la chip
               ( flex Bulma ) reste intact -> la croix de fermeture
               garde sa place naturelle à droite. -->
          <span class="oa-chip-text">{{ tag }}</span>
          <button
            v-if="!hasSingleOption"
            type="button"
            class="delete is-small"
            :aria-label="`delete tag ${tag}`"
            tabindex="0"
            @click.stop="removeTag(tag)"
            @keyup.enter.stop="removeTag(tag)"
          />
        </li>
      </ul>
      <div
        v-if="!hasSingleOption"
        class="text-filter-input-row"
      >
        <span class="icon is-small is-left">
          <font-awesome-icon icon="search" />
        </span>
        <input
          ref="inputRef"
          v-model="newTag"
          type="search"
          class="input is-small"
          autocomplete="off"
          :placeholder="placeholder"
          :aria-label="placeholder"
          @keydown.enter.prevent="commitNewTag"
          @blur="commitNewTagOnBlur"
        >
      </div>
    </div>
    <!--
      Bouton dédié pour ajouter un filtre "(vide)" . Délégué au composant
      partagé `EmptyValueButton` ( DRY : même UX sur TextFilter et
      ReferenceFilter ) . Caché tant que la colonne ne contient pas de
      valeur vide en base ( hasEmpty propagé via filterList[componentKey]
      .hasEmpty ) , ou tant que la sélection courante contient déjà
      "(vide)" ou que la colonne est en mode single-option .
    -->
    <EmptyValueButton
      :has-empty="hasEmpty"
      :disabled="isEmptyValueSelected || hasSingleOption"
      @add="addEmptyValue"
    />
  </div>
</template>

<script lang="ts">
import { computed, ref, watch } from "vue";
import i18n from "@/i18n";
import EmptyValueButton from "@/components/common/EmptyValueButton.vue";

/**
 * Filtre de recherche textuelle libre sur une colonne marquée
 * {@code __FILTER_TEXT__} ( le parent {@link FiltersDataCollapse} fait
 * l'aiguillage entre {@link TextFilter} et {@link ListFilter} selon le
 * tag posé en YAML ).
 *
 * <p><b>Sémantique de recherche</b> : "contient" insensible à la casse.
 * Côté wire , le payload émis porte {@code isRegExp: true} pour que le
 * backend route vers {@link ComponentFiltersForWordByRegexp} ( JSONPath
 * {@code @ like_regex flag "i"} ). La saisie utilisateur est échappée
 * des méta-regex avant émission ; l'utilisateur ne manipule jamais une
 * regex , il tape une portion de texte qui sera matchée comme un
 * fragment.
 *
 * <p><b>Séparateurs</b> : seul {@code Enter} valide la chip. Les
 * caractères {@code , ; |} sont conservés littéralement dans la chaîne
 * recherchée ( cf. issue #1 ). Cela permet de chercher des fragments
 * comme "Smith, J." ou "a;b" sans qu'ils soient découpés.
 *
 * <p><b>Multi-chips</b> : on peut empiler autant de chips que voulu (
 * sémantique OR côté backend : la ligne matche si au moins une chip
 * matche ). L'état est purement local ; aucune dépendance à un model
 * externe interne ( cf. issue #2 sur b-taginput ).
 *
 * <p><b>Valeurs vides</b> : un bouton dédié *"(vide)"* permet de
 * sélectionner les lignes où la colonne est null en base. Sur le wire
 * cette sélection est représentée par {@code null} dans le tableau
 * {@code filters} ; cf. §5.7 de FILTER_TEXT_LIST.md.
 */
export default {
  name: "TextFilter",
  components: { EmptyValueButton },
  props: {
    column: { type: Object, required: true },
    currentFilter: { type: Object, default: () => null },
    placeholder: { type: String, default: "" },
    /**
     * Vrai si la colonne contient au moins une valeur vide ( null ) en
     * base. Calculé côté backend ( EXISTS rapide , cf.
     * DataRepository.getColumnHasEmpty ) puis transmis via
     * filterList[componentKey].hasEmpty. Conditionne l'affichage du
     * bouton "+ (vide)" pour éviter de proposer une option qui
     * n'aurait jamais de match.
     */
    hasEmpty: { type: Boolean, default: false },
    /**
     * Si la colonne ne contient qu'une seule valeur distincte non-null
     * en base , cette prop la porte ; sinon {@code null}. En présence
     * d'une single-value , le composant bascule en mode auto-select
     * non-supprimable ( pattern aligné sur ListFilter / ReferenceFilter ).
     */
    singleValue: { type: String as () => string | null, default: null },
  },
  emits: ["update-text-filter"],
  setup(props, { emit }) {
    /**
     * Limite haute affichée avant l'apparition du scroll.
     * 3 chips × 4 lignes = 12. Au-delà , la zone devient scrollable.
     */
    const MAX_VISIBLE_ROWS = 4;
    const CHIPS_PER_ROW = 3;
    const OVERFLOW_THRESHOLD = MAX_VISIBLE_ROWS * CHIPS_PER_ROW;

    /**
     * Libellé i18n affiché pour la chip "(vide)" et utilisé comme
     * marqueur interne pour la convertir en {@code null} à l'émission.
     */
    const emptyValueLabel = computed(() => i18n.global.t("filter.empty-value-label"));

    /**
     * Échappe les méta-regex d'une chaine pour que le backend la traite
     * comme du texte littéral via {@code like_regex} ( mode "contient" ).
     * Caractères concernés : {@code . ( ) [ ] { } * + ? ^ $ | \ }.
     */
    function escapeRegex(s: string): string {
      return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    /**
     * État local : valeurs telles qu'affichées dans les chips. Pour la
     * sentinelle "(vide)" on stocke le libellé traduit ( pas {@code null} )
     * pour cohérence avec le wire format ( null <-> libellé ).
     */
    const selectedValues = ref<string[]>([]);

    /** Saisie courante dans le champ texte ( pas encore validée ). */
    const newTag = ref<string>("");

    /** Ref DOM sur l'input pour récupérer le focus après commit. */
    const inputRef = ref<HTMLInputElement | null>(null);

    /**
     * Reconstruit l'état d'affichage à partir du {@code currentFilter}
     * envoyé par le parent. Le wire format porte {@code null} pour
     * "(vide)" - on traduit en libellé ; pour les chips texte , on
     * affiche la chaine telle qu'on la stockait ( raison : on ne peut
     * pas inverser proprement l'échappement regex côté affichage ,
     * donc le parent ne renvoie que ce qu'on lui a envoyé en
     * {@code displayValues} - voir {@code emitUpdate} ).
     */
    function syncFromParent(filter: any) {
      const filters = filter?.filters as Array<string | null> | undefined;
      const displays = filter?.displayValues as string[] | undefined;
      // Si le parent garde notre dernier displayValues , on le reprend
      // tel quel ( pas d'inversion lossy de l'échappement regex ).
      if (Array.isArray(displays)) {
        selectedValues.value = [...displays];
        return;
      }
      // Sinon ( reset / chargement initial ) on retombe sur le wire :
      // null -> libellé "(vide)" , chaine -> chaine.
      selectedValues.value = (filters || []).map(v =>
        v === null ? emptyValueLabel.value : v as string,
      );
    }

    syncFromParent(props.currentFilter);

    /**
     * Vrai si la colonne n'a qu'une seule valeur distincte ; le composant
     * bascule alors en mode auto-select non-supprimable , aligné sur
     * ReferenceFilter / ListFilter ( pattern repris de §F5 + visio ).
     */
    const hasSingleOption = computed(() => props.singleValue !== null);

    /**
     * Auto-pré-sélection au montage : si la colonne ne contient qu'une
     * seule valeur distincte et qu'aucun filtre courant n'est défini ,
     * on positionne la chip auto-sélectionnée et on émet le filtre. Le
     * pattern setTimeout(0) évite de court-circuiter le watch initial
     * du parent ( cf. ReferenceFilter ligne 121-123 ).
     */
    if (hasSingleOption.value && selectedValues.value.length === 0) {
      selectedValues.value = [props.singleValue as string];
      setTimeout(() => emitUpdate(), 0);
    }

    const isOverflowing = computed(
      () => selectedValues.value.length > OVERFLOW_THRESHOLD,
    );

    const isEmptyValueSelected = computed(
      () => selectedValues.value.includes(emptyValueLabel.value),
    );

    // Re-sync depuis le parent ( ex. clear filters ). Pour éviter les
    // boucles , on ne réassigne que si le contenu a réellement changé.
    watch(
      () => props.currentFilter,
      (newVal) => {
        const expected = (newVal?.filters as Array<string | null> | undefined || [])
          .map(v => v === null ? emptyValueLabel.value : v as string);
        if (JSON.stringify(expected) !== JSON.stringify(selectedValues.value)) {
          syncFromParent(newVal);
        }
      },
      { deep: true },
    );

    /**
     * Convertit l'état d'affichage vers le wire format puis émet le
     * payload. Le contrat avec {@code FiltersDataCollapse} :
     *   - {@code filters} : array final attendu par le backend
     *     ( chaines regex-échappées + null pour "(vide)" )
     *   - {@code isRegExp: true} : route le backend vers Regexp
     *   - {@code displayValues} : transmis pour que le parent puisse
     *     nous le ré-injecter à l'identique sans avoir à inverser
     *     l'échappement regex
     */
    function emitUpdate() {
      const wireFilters = selectedValues.value.map(v =>
        v === emptyValueLabel.value ? null : escapeRegex(v),
      );
      emit("update-text-filter", {
        columns: props.column,
        componentKey: props.column.componentKey,
        filters: wireFilters,
        displayValues: [...selectedValues.value],
        isRegExp: true,
      });
    }

    /**
     * Valide la saisie courante et l'ajoute en chip , si elle est non
     * vide ET non déjà présente. Réinitialise toujours le champ pour
     * permettre un nouvel ajout immédiat ( y compris la même valeur si
     * elle a été supprimée juste avant - cf. issue #2 du legacy ).
     *
     * Longueur minimale : on impose 2 caractères ( après trim ) pour
     * éviter les recherches trop ouvertes sur 1 seul caractère ( ex:
     * "a" -> match énorme et coûteux côté JSONPath ). La contrainte
     * est mentionnée dans l'infobulle d'aide ( clé i18n
     * `filter.text-tooltip-help` ) pour informer l'utilisateur .
     */
    const MIN_CHIP_LENGTH = 2;

    /** Tente d'ajouter la chaine candidate comme chip ; retourne false
     *  si trop courte ou doublon. Pas d'effet sur le focus. */
    function tryAppendChip(candidate: string): boolean {
      if (candidate.length < MIN_CHIP_LENGTH) return false;
      if (selectedValues.value.includes(candidate)) return false;
      selectedValues.value = [...selectedValues.value, candidate];
      emitUpdate();
      return true;
    }

    /**
     * Valide la saisie courante via Enter : on commit puis on rend le
     * focus à l'input pour permettre l'enchainement immédiat ( taper
     * Enter , taper la chip suivante , Enter , ... ).
     */
    function commitNewTag() {
      const candidate = (newTag.value || "").trim();
      newTag.value = "";
      tryAppendChip(candidate);
      setTimeout(() => inputRef.value?.focus(), 0);
    }

    /**
     * Validation auto sur perte de focus : si l'utilisateur a tapé
     * quelque chose et clique ailleurs sans presser Enter , on commit
     * la chip pour ne pas perdre sa saisie ( comportement legacy
     * b-taginput attendu ). Pas de refocus ici - l'utilisateur a
     * cliqué ailleurs , le ramener serait un piège UX.
     */
    function commitNewTagOnBlur() {
      const candidate = (newTag.value || "").trim();
      newTag.value = "";
      tryAppendChip(candidate);
    }

    /**
     * Suppression d'une chip via la croix. Met à jour l'état local
     * et émet ; le focus revient sur l'input pour permettre d'ajouter
     * immédiatement une autre valeur sans clic intermédiaire.
     */
    function removeTag(tag: string) {
      if (hasSingleOption.value) return;
      selectedValues.value = selectedValues.value.filter(v => v !== tag);
      emitUpdate();
      setTimeout(() => inputRef.value?.focus(), 0);
    }

    /** Ajoute la chip "(vide)" si elle n'est pas déjà présente. */
    function addEmptyValue() {
      if (!isEmptyValueSelected.value && !hasSingleOption.value) {
        selectedValues.value = [...selectedValues.value, emptyValueLabel.value];
        emitUpdate();
      }
    }

    return {
      selectedValues,
      newTag,
      inputRef,
      emptyValueLabel,
      isEmptyValueSelected,
      isOverflowing,
      hasSingleOption,
      commitNewTag,
      commitNewTagOnBlur,
      removeTag,
      addEmptyValue,
    };
  },
};
</script>

<style scoped>
/*
 * Wrapper des chips + champ de saisie. Layout grid 3 colonnes pour
 * les chips , identique au comportement legacy : largeur déterministe
 * et alignement à gauche , les valeurs longues tronquées avec ellipsis.
 */
.text-filter .taginput-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 8px;
  background: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  min-height: 2.25em;
}
.text-filter .taginput-container.is-disabled {
  background: #fafafa;
  cursor: not-allowed;
}

/*
 * Layout flex-wrap , chip sizée au contenu ( comme ListFilter qui
 * fonctionne ) , avec :
 *   - min-width 8em sur chaque chip → même un contenu très court
 *     ( ex: "A" ) reste lisible et ne se ratatine pas à ~3em ;
 *   - max-width 20em sur le span de texte interne ( cf. .oa-chip-text
 *     plus bas ) → contenus longs tronqués + tooltip natif ;
 *   - les chips débordent en flex-wrap sur la ligne suivante , pas
 *     sur le champ voisin.
 *
 * Cohérent avec le rendu des chips ListFilter / ReferenceFilter qui
 * ne forcent pas non plus de largeur fixe.
 */
.text-filter-chips {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-content: flex-start;
}
.text-filter-chips .tag {
  min-width: 8em;
}

/*
 * UX longues valeurs : on cappe uniquement le TEXTE à 20em maxi via
 * .oa-chip-text ( ellipse + tooltip natif sur le <li> qui porte
 * l'attribut title ). La chip elle-même conserve son layout flex
 * Bulma par défaut , la croix .delete reste à sa place naturelle à
 * droite. Aucun !important , aucun override de display/width/flex
 * sur la chip → comportement identique à ReferenceFilter.
 */
/*
 * Span interne pour la troncature : limite le TEXTE à 20em maxi
 * ( ellipse + tooltip natif via title sur le <li> ). La chip
 * elle-même est sizée naturellement par Bulma au contenu , donc
 * une chip courte reste petite ( + min-width 8em sur .tag ) et
 * une chip longue plafonne à ~20em. Identique à ListFilter.
 */
.oa-chip-text {
  display: inline-block;
  max-width: 20em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

/*
 * Au-delà du seuil ( MAX_VISIBLE_ROWS × CHIPS_PER_ROW = 12 ) , on
 * plafonne la hauteur à 4 lignes de chips et on active la scrollbar.
 */
.text-filter.is-overflowing .text-filter-chips {
  max-height: 9em;
  overflow-y: auto;
  scrollbar-gutter: stable;
}
.text-filter.is-overflowing .text-filter-chips::-webkit-scrollbar {
  width: 8px;
}
.text-filter.is-overflowing .text-filter-chips::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}
.text-filter.is-overflowing .text-filter-chips::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

/*
 * Champ de saisie ( input + icône loupe ) sur une ligne dédiée , sous
 * les chips. Utilise les classes Bulma {@code .input.is-small} pour
 * homogéniser avec le reste de l'app.
 */
.text-filter-input-row {
  position: relative;
  display: flex;
  align-items: center;
}
.text-filter-input-row .icon.is-left {
  position: absolute;
  left: 0.4em;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #7a7a7a;
}
/*
 * padding-left dimensionné pour : icone ( ~1em ) + offset gauche ( 0.4em )
 *   + un GAP visible ( ~0.6em ) entre la loupe et le placeholder / le
 *   texte saisi. Sans ce gap , la loupe collait au "S" de "Search...".
 */
.text-filter-input-row .input {
  width: 100%;
  padding-left: 2em;
  height: 2em;
  border: none;
  box-shadow: none;
  background: transparent;
  outline: none;
  text-overflow: ellipsis;
}
.text-filter-input-row .input:focus {
  box-shadow: none;
}

/*
 * Bouton "+ (vide)" rendu sous le bloc chips , aligné à gauche , discret
 * mais cliquable. Style cohérent avec les liens de l'app.
 */
.empty-value-trigger {
  display: inline-block;
  margin-top: 0.35rem;
  font-size: 0.85rem;
  color: var(--theme-primary, #167df0);
  cursor: pointer;
}
.empty-value-trigger:hover {
  text-decoration: underline;
}

/*
 * Mode "single-option" ( colonne ne contenant qu'une seule valeur
 * distincte non-null ) : on grise la croix de la chip pour signaler
 * son caractère non-supprimable. Pattern repris de ReferenceFilter /
 * ListFilter pour cohérence visuelle.
 */
.single-option-tag .delete {
  background-color: #b9bbbb;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.6;
}
</style>
